'use client'

import { datadogRum } from '@datadog/browser-rum'

import { env } from '~/env.js'
import { isFrontEndTelemetryEnabled } from '~/utils/is-front-end-telemetry-enabled.ts'

type PropagatorType = 'tracecontext' | 'datadog' | 'b3' | 'b3multi'

// Initialize Datadog RUM at module level.
// Module-level code in a 'use client' file only runs once in the browser,
// providing natural idempotency without needing an explicit guard.
const clientToken = env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
const applicationId = env.NEXT_PUBLIC_DATADOG_APPLICATION_ID

function parseSampleRate(value: string | undefined, fallback: number): number {
  if (!value) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100
    ? parsed
    : fallback
}

if (isFrontEndTelemetryEnabled() && clientToken && applicationId) {
  const site = env.NEXT_PUBLIC_DATADOG_SITE ?? 'datadoghq.com'
  const service = env.NEXT_PUBLIC_DATADOG_SERVICE ?? 'magicsky-frontend'
  const sessionSampleRate = parseSampleRate(
    env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
    100
  )
  const sessionReplaySampleRate = parseSampleRate(
    env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    100
  )
  const traceSampleRate = parseSampleRate(
    env.NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE,
    100
  )
  const allowedTracingUrls = (
    env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACING_URLS?.split(',') ?? []
  )
    .map((value) => value.trim())
    .filter(Boolean)
  const origin = window.location.origin

  const propagatorTypes: PropagatorType[] = ['tracecontext', 'datadog']

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    // we don't register OTel instrumentation for local envs, so we should never see env = local,
    // but a fallback we can flag if we ever see it
    env: env.NEXT_PUBLIC_DATADOG_ENV ?? 'local',
    version: env.NEXT_PUBLIC_DATADOG_VERSION,
    sessionSampleRate,
    sessionReplaySampleRate,
    traceSampleRate,
    // Datadog RUM should own frontend trace propagation.
    // Default to same-origin API calls and allow env-based extensions.
    allowedTracingUrls: [
      ...allowedTracingUrls.map((url) => ({
        match: url,
        propagatorTypes,
      })),
      {
        match: (url: string) => {
          if (url.startsWith('/api/')) {
            return true
          }

          try {
            const parsedUrl = new URL(url, origin)
            return (
              parsedUrl.origin === origin &&
              parsedUrl.pathname.startsWith('/api/')
            )
          } catch {
            return false
          }
        },
        propagatorTypes,
      },
    ],
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: (event, context) => {
      // Enrich error events with dd_context attached to the original error
      // @see https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/advanced_configuration/?tab=npm#error-context
      if (event.type === 'error' && context && 'error' in context) {
        const error = context.error as
          | (Error & { dd_context?: Record<string, unknown> })
          | undefined
        if (error?.dd_context) {
          event.context = { ...event.context, ...error.dd_context }
        }
        // Attach the error digest (Next.js redacts server errors and sends a digest)
        if (error && 'digest' in error && typeof error.digest === 'string') {
          event.context = { ...event.context, digest: error.digest }
        }
      }
      return true
    },
  })
}

/**
 * Datadog RUM (Real User Monitoring) component.
 * Importing this component triggers RUM initialization via module-level code above.
 * The component itself renders nothing.
 */
export function DatadogRUM() {
  return null
}
